import { LoaderCircleV2, UI_CAPTION, colors2 } from "oolib";
import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { SimpleBarChart } from "./comps/SimpleBarChart";
import { DashboardWidgetShell } from "./comps/DashboardWidgetShell";
import { CardAnnoExplorer } from "../../components/analysisModules/AnnotationExplorer/comps/CardAnnoExplorer";
import { SidePanel } from "@/components/UI/SidePanel";
import { useActivePanel } from "./comps/ActivePanelContext";
import { useGetSentimentTopics } from "./hooks/useGetSentimentTopics";
import { useGetAnnotationSentimentTopics } from "./hooks/useGetAnnotationSentimentTopics";

const NON_MEANINGFUL_VALUES = new Set(["Non-meaningful Responses", "Data Quality Issues"]);

const SENTIMENT_CARD_CONFIGS = {
  annoTagsTitle: "NLP Tags",
  metaBlock: [],
};

const buildChunkTags = (chunk) => {
  const tags = {};
  if (chunk.sentiment) {
    const parts = [chunk.sentiment.emotion, chunk.sentiment.polarity].filter(Boolean);
    if (parts.length) {
      tags.sentiment = {
        collectionId: "Sentiment",
        data: parts.map((v) => ({ display: v.charAt(0).toUpperCase() + v.slice(1), _id: v })),
      };
    }
  }
  if (chunk.topics?.length) {
    tags.topics = {
      collectionId: "AI Topics",
      data: chunk.topics.map((t) => ({
        display: t.parentTopic ? `${t.name} (${t.parentTopic})` : t.name,
        _id: t.name,
      })),
    };
  }
  if (chunk.entities?.length) {
    tags.entities = {
      collectionId: "Named Entities",
      data: chunk.entities.map((e) => ({
        display: `${e.name} [${e.type}]`,
        _id: e.name,
      })),
    };
  }
  return tags;
};

const chunkToAnnoDoc = (chunk) => ({
  ...chunk,
  main: { ...chunk.main, title: chunk.main?.title || "Untitled" },
  annotations: {
    ...chunk.annotations,
    annoKey: chunk._id,
    tags: buildChunkTags(chunk),
  },
  fills: [],
});

// Fetches raw annotation docs by IDs for drill-down
const AnnotationDrillDown = ({ annotationIds }) => {
  const { data: annotations, isLoading } = useQuery(
    ["annotationSentimentDrillDown", annotationIds?.join(",")],
    () =>
      axios
        .get("/api/playground/annotationSentimentTopics/annotations", {
          params: { ids: annotationIds.join(",") },
        })
        .then((res) => res.data.annotations || []),
    { enabled: !!annotationIds?.length, staleTime: 30 * 60 * 1000 }
  );

  if (isLoading) return <div style={{ padding: "1rem", textAlign: "center" }}><LoaderCircleV2 /></div>;
  if (!annotations?.length) return <UI_CAPTION color={colors2.grey40}>No annotations found</UI_CAPTION>;

  return (
    <>
      {annotations.map((anno, i) => (
        <CardAnnoExplorer
          key={anno._id || i}
          singleAnno={anno}
          cardConfigs={{ metaBlock: [] }}
          cardShellStyle={{ backgroundColor: colors2.grey2 }}
        />
      ))}
    </>
  );
};

export const SentimentTopicsWidget = ({ polarity, title, color, useAnnotations = false }) => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const { isOpen: listPanelOpen, open: openListPanel, close: closeListPanel } =
    useActivePanel(`sentiment-topics:${polarity}`);

  // Chunk-based (old) or annotation-based (new) depending on flag
  const chunkQuery = useGetSentimentTopics({ polarity });
  const annoQuery = useGetAnnotationSentimentTopics({ polarity });
  const activeQuery = useAnnotations ? annoQuery : chunkQuery;

  const { isLoading, isError } = activeQuery;
  const allChunks = useAnnotations ? [] : (activeQuery.data || []);
  const annoTopics = useAnnotations ? (activeQuery.data?.topics || []) : [];
  const loading = isLoading;

  const topics = useMemo(() => {
    if (isError) return [];
    if (isLoading) return null;

    // Annotation-based: backend already returns sorted topics
    if (useAnnotations) return annoTopics;

    // Chunk-based: compute from raw chunks
    const counts = {};
    for (const chunk of allChunks) {
      for (const t of chunk.topics || []) {
        if (NON_MEANINGFUL_VALUES.has(t.name) || NON_MEANINGFUL_VALUES.has(t.parentTopic)) continue;
        const label = t.parentTopic ? `${t.name} (${t.parentTopic})` : t.name;
        counts[label] = (counts[label] || 0) + 1;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [allChunks, annoTopics, isLoading, isError, useAnnotations]);

  useEffect(() => {
    if (!listPanelOpen) setSelectedTopic(null);
  }, [listPanelOpen]);

  const maxCount = topics?.[0]?.count || 1;
  const topTopics = topics?.slice(0, 8) || [];

  const getChunksForTopic = (topicName) => {
    return allChunks.filter((c) =>
      (c.topics || []).some((t) => {
        const label = t.parentTopic ? `${t.name} (${t.parentTopic})` : t.name;
        return label === topicName;
      })
    );
  };

  const handleCloseListPanel = () => {
    closeListPanel();
    setSelectedTopic(null);
  };

  return (
    <>
      <div
        style={{ minWidth: 280, cursor: topics?.length ? "pointer" : "default" }}
        onClick={() => topics?.length && openListPanel()}
      >
        <div style={{ pointerEvents: "none" }}>
          <DashboardWidgetShell title={title}>
            {loading ? (
              <div style={{ padding: "1rem", textAlign: "center" }}><LoaderCircleV2 /></div>
            ) : !topics?.length ? (
              <UI_CAPTION color={colors2.grey40}>No data available</UI_CAPTION>
            ) : (
              <SimpleBarChart
                data={topTopics}
                barColor={color}
                barHeight={26}
                barGap={2}
              />
            )}
          </DashboardWidgetShell>
        </div>
      </div>

      {/* Level 1: All topics list */}
      <SidePanel
        isOpen={listPanelOpen}
        onClose={handleCloseListPanel}
        title={title}
        rightOffset={selectedTopic ? 350 : 0}
        closeOnOutsideClick={!selectedTopic}
        bodyStyle={{ padding: "0px 14px" }}
      >
        <SimpleBarChart
          data={topics || []}
          barColor={color}
          barHeight={26}
          barGap={2}
          activeName={selectedTopic}
          onClick={(v) => setSelectedTopic(selectedTopic === v.name ? null : v.name)}
        />
      </SidePanel>

      {/* Level 2: Topic detail */}
      <SidePanel
        isOpen={!!selectedTopic}
        onClose={() => setSelectedTopic(null)}
        title={selectedTopic || ""}
        zIndex={1001}
        bodyStyle={{
          padding: "0 14px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {selectedTopic && useAnnotations ? (
          <AnnotationDrillDown
            annotationIds={annoTopics.find((t) => t.name === selectedTopic)?.annotationIds || []}
          />
        ) : selectedTopic ? (
          getChunksForTopic(selectedTopic).map((c, i) => (
            <CardAnnoExplorer
              key={c._id || i}
              singleAnno={chunkToAnnoDoc(c)}
              cardConfigs={SENTIMENT_CARD_CONFIGS}
              cardShellStyle={{ backgroundColor: colors2.grey2 }}
              scrollIntoViewElemIdOverride={c.meta?.valuePath}
            />
          ))
        ) : null}
      </SidePanel>
    </>
  );
};
